import { useState, useCallback, useMemo, RefObject } from "react";

export interface TourStep {
  id: string;
  targetSelector?: string;
  targetRef?: RefObject<HTMLElement>;
  title: string;
  description: string;
  position?: "top" | "bottom" | "left" | "right" | "center";
  offset?: { x: number; y: number };
  highlightPadding?: number; // Custom padding for highlight box (default: 8)
}

export interface TourGuideConfig {
  moduleId: string;
  steps: TourStep[];
  enabled?: boolean;
}

export interface UseTourGuideReturn {
  isActive: boolean;
  currentStep: number;
  currentStepData: TourStep | null;
  totalSteps: number;
  startTour: () => void;
  stopTour: () => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (stepIndex: number) => void;
}

export const BG_OVERLAY = {
  BACKGROUND: "rgba(0, 0, 0, 0.5)",
  BACKDROP_FILTER: "blur(1px)",
};

const TOUR_GUIDE_SCROLL_WAIT_TIME = 0; // ms to wait after scroll

export const useTourGuide = (
  config: TourGuideConfig
): UseTourGuideReturn => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(0);

  const { moduleId, steps, enabled = true } = config;

  const currentStepData = useMemo(() => {
    if (!isActive || currentStep >= steps.length) {
      return null;
    }
    return steps[currentStep];
  }, [isActive, currentStep, steps]);

  const startTour = useCallback(() => {
    if (!enabled || steps.length === 0) {
      return;
    }

    // Scroll to top of the main container (adapt selector to your layout)
    const rightBox = document.querySelector(".right-box");
    if (rightBox) {
      rightBox.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    // Wait for scroll to complete
    setTimeout(() => {
      setCurrentStep(0);
      setIsActive(true);
    }, TOUR_GUIDE_SCROLL_WAIT_TIME);
  }, [enabled, steps.length]);

  const stopTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      // Last step - close tour
      stopTour();
    }
  }, [currentStep, steps.length, stopTour]);

  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback(
    (stepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < steps.length) {
        setCurrentStep(stepIndex);
      }
    },
    [steps.length]
  );

  return {
    isActive,
    currentStep,
    currentStepData,
    totalSteps: steps.length,
    startTour,
    stopTour,
    nextStep,
    previousStep,
    goToStep,
  };
};
